import { useNavigate } from 'react-router-dom';
import { LogOut, ArrowUpRight, ChevronDown, Zap, Menu, X } from 'lucide-react';
import { useAuth } from '@/components/auth/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { loadLensJsonProperties } from '@/lib/lens-json-properties';
import { useCredits } from '@/hooks/useCredits';
import logo from '@/assets/logo.svg';
import { useEffect, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  getIndustryAnchorId,
  getCanonicalIndustry,
  getIndustryMenuLabel,
  getIndustrySlug,
  industrySort,
} from '@/lib/industries';

interface HeaderProps {
  showNavigation?: boolean;
  /** Slim single-row header used on working screens (App editor, App run) to
   *  leave maximum vertical space for the App content. */
  compact?: boolean;
  hideUnauthenticatedActions?: boolean;
  hideAuthenticatedActions?: boolean;
}

type HeaderProfile = {
  is_admin?: boolean | null;
  email?: string | null;
};

type HeaderLensRow = {
  id: string;
  status?: string | null;
  author_email?: string | null;
};

const isVisibleLensForProfile = (
  method: HeaderLensRow,
  profile: HeaderProfile | null,
) => {
  const isAdmin = profile?.is_admin === true;
  const userEmail = (profile?.email || '').toLowerCase();
  const authorEmail = (method.author_email || '').toLowerCase();

  switch (method.status) {
    case 'draft':
      return false;
    case 'admins':
      return isAdmin;
    case 'author':
      return isAdmin || (!!authorEmail && authorEmail === userEmail);
    case 'published':
      return true;
    default:
      return false;
  }
};

export const Header = ({ showNavigation = true, compact = false, hideUnauthenticatedActions = false, hideAuthenticatedActions = false }: HeaderProps) => {
  const navigate = useNavigate();
  const { user, isAuthor, isAnonymous, signOut } = useAuth();
  const { credits, loading: creditsLoading } = useCredits();
  const [isAdmin, setIsAdmin] = useState(false);
  const [industries, setIndustries] = useState<string[]>([]);
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileIndustriesOpen, setMobileIndustriesOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchHeaderData = async () => {
      let profile: HeaderProfile | null = null;
      if (user && !isAnonymous) {
        const { data, error } = await supabase
          .from('profiles')
          .select('is_admin, email')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!error && data) {
          profile = data;
        }
      }

      if (cancelled) return;
      setIsAdmin(profile?.is_admin === true);

      const { data: methodsData, error: methodsError } = await supabase
        .from('thinking_methods')
        .select('id, status, author_email')
        .order('order_index', { ascending: true });

      if (cancelled) return;

      if (methodsError) {
        console.warn('Unable to load lenses for header navigation:', methodsError);
        setIndustries([]);
        return;
      }

      const visibleMethods = ((methodsData || []) as HeaderLensRow[])
        .filter((method) => isVisibleLensForProfile(method, profile));
      const visibleMethodIds = visibleMethods.map((method) => method.id);

      let attrsData: Array<{ thinking_method_id: string; attribute_value: string }> = [];
      if (visibleMethodIds.length > 0) {
        const { data, error } = await loadLensJsonProperties(visibleMethodIds);

        if (cancelled) return;

        if (error) {
          console.warn('Unable to load lens industries for header navigation:', error);
          setIndustries([]);
          return;
        }

        attrsData = (data || []) as Array<{ thinking_method_id: string; attribute_value: string }>;
      }

      if (cancelled) return;

      const industryByLensId = new Map<string, string>();
      for (const attr of attrsData) {
        try {
          const parsed = JSON.parse(attr.attribute_value);
          const canonicalIndustry = getCanonicalIndustry(parsed.industry);
          if (canonicalIndustry) {
            industryByLensId.set(attr.thinking_method_id, canonicalIndustry);
          }
        } catch (error) {
          console.warn('Ignoring invalid lens JSON properties for header navigation:', error);
        }
      }

      const industrySet = new Set<string>();
      for (const method of visibleMethods) {
        const industry = industryByLensId.get(method.id);
        if (industry) industrySet.add(industry);
      }

      setIndustries(Array.from(industrySet).sort(industrySort));
    };

    fetchHeaderData();

    return () => {
      cancelled = true;
    };
  }, [user, isAnonymous]);

  const handleSignIn = () => {
    navigate('/auth');
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleAdmin = () => {
    navigate('/admin');
  };

  const handleExploreLenses = () => {
    navigate('/beta_access');
  };

  const handleIndustryClick = (industry: string) => {
    setIndustriesOpen(false);
    const slug = getIndustrySlug(industry);
    navigate(`/lenses#${slug}`);
    // If already on /lenses, scroll manually
    setTimeout(() => {
      const el = document.getElementById(getIndustryAnchorId(industry));
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const goto = (path: string) => {
    setMobileOpen(false);
    navigate(path);
  };

  const handleMobileIndustryClick = (industry: string) => {
    setMobileIndustriesOpen(false);
    setMobileOpen(false);
    const slug = getIndustrySlug(industry);
    navigate(`/lenses#${slug}`);
    setTimeout(() => {
      const el = document.getElementById(getIndustryAnchorId(industry));
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  if (compact) {
    return (
      <header className="sticky top-0 z-50 bg-[#050d1f] shadow-md">
        <div className="flex h-12 w-full items-center gap-3 px-3 sm:px-4">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[85vw] max-w-sm overflow-y-auto border-white/10 bg-[#050d1f] p-0 text-white">
              <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
                <img src={logo} alt="Applyt" className="h-8 w-auto brightness-0 invert" />
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md text-white hover:bg-white/10"
                  aria-label="Close menu"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              {showNavigation && (
                <nav className="flex flex-col p-2 text-sm">
                  <button onClick={() => goto('/')} className="rounded-md px-3 py-3 text-left text-xs uppercase tracking-[0.12em] text-white/80 hover:bg-white/10">Home</button>
                  {industries.length > 0 && (
                    <>
                      <button
                        onClick={() => setMobileIndustriesOpen(!mobileIndustriesOpen)}
                        className="flex items-center justify-between rounded-md px-3 py-3 text-xs uppercase tracking-[0.12em] text-white/80 hover:bg-white/10"
                      >
                        Industry
                        <ChevronDown className={`h-3 w-3 transition-transform ${mobileIndustriesOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {mobileIndustriesOpen && (
                        <div className="flex flex-col pl-3">
                          {industries.map((industry) => (
                            <button
                              key={industry}
                              onClick={() => handleMobileIndustryClick(industry)}
                              className="rounded-md px-3 py-2 text-left text-[11px] uppercase tracking-[0.12em] text-white/70 hover:bg-white/10"
                            >
                              {getIndustryMenuLabel(industry)}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  <button onClick={() => goto('/expert-boards')} className="rounded-md px-3 py-3 text-left text-xs uppercase tracking-[0.12em] text-white/80 hover:bg-white/10">My Boards</button>
                  {user && !isAnonymous && <button onClick={() => goto('/my-conversations')} className="rounded-md px-3 py-3 text-left text-xs uppercase tracking-[0.12em] text-white/80 hover:bg-white/10">Conversations</button>}
                  <button onClick={() => goto('/templates')} className="rounded-md px-3 py-3 text-left text-xs uppercase tracking-[0.12em] text-white/80 hover:bg-white/10">Templates</button>
                  <button onClick={() => goto('/applications')} className="rounded-md px-3 py-3 text-left text-xs uppercase tracking-[0.12em] text-white/80 hover:bg-white/10">My Apps</button>
                  <button onClick={() => goto(isAuthor ? '/for_expert' : '/become_expert')} className="rounded-md px-3 py-3 text-left text-xs uppercase tracking-[0.12em] text-white/80 hover:bg-white/10">For Experts</button>
                  {isAdmin && (
                    <button onClick={() => goto('/admin')} className="rounded-md px-3 py-3 text-left text-xs uppercase tracking-[0.12em] text-white/80 hover:bg-white/10">Admin</button>
                  )}
                </nav>
              )}
            </SheetContent>
          </Sheet>

          <button
            type="button"
            onClick={() => navigate('/')}
            className="inline-flex shrink-0 items-center focus:outline-none"
            aria-label="Applyt home"
          >
            <img src={logo} alt="Applyt" className="h-6 w-auto brightness-0 invert" />
          </button>

          <div className="ml-auto flex items-center gap-2">
            {user && !isAnonymous ? (
              !hideAuthenticatedActions && (
                <>
                  <button
                    onClick={() => navigate('/account')}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] font-medium text-white transition-colors hover:bg-white/20"
                    title="View account & credits"
                  >
                    <Zap className="h-3 w-3 text-yellow-300" />
                    {creditsLoading ? '…' : credits}
                  </button>
                  <span className="hidden max-w-[180px] truncate text-[12px] text-white/60 lg:inline">{user.email}</span>
                  <button
                    onClick={handleSignOut}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-white/80 hover:bg-white/10 hover:text-white"
                    aria-label="Sign out"
                    title="Sign out"
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </>
              )
            ) : (
              !hideUnauthenticatedActions && (
                <button
                  onClick={handleSignIn}
                  className="text-[12px] uppercase tracking-[0.15em] text-white/80 transition-colors hover:text-white"
                >
                  Sign In
                </button>
              )
            )}
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="sticky top-0 z-50 bg-[#050d1f] shadow-md">
      {/* Top tier: Logo + CTA */}
      <div className="border-b border-white/10 bg-[#050d1f]/95">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 md:py-4 flex items-center justify-between md:justify-center gap-2 relative">
          {/* Mobile menu button */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-md text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[85vw] max-w-sm bg-[#050d1f] border-white/10 text-white p-0 overflow-y-auto">
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                <img src={logo} alt="Applyt" className="h-8 w-auto brightness-0 invert" />
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md text-white hover:bg-white/10"
                  aria-label="Close menu"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {showNavigation && (
                <nav className="flex flex-col p-2 text-sm">
                  <button onClick={() => goto('/')} className="text-left px-3 py-3 rounded-md hover:bg-white/10 uppercase tracking-[0.12em] text-xs text-white/80">Home</button>

                  {industries.length > 0 && (
                    <>
                      <button
                        onClick={() => setMobileIndustriesOpen(!mobileIndustriesOpen)}
                        className="flex items-center justify-between px-3 py-3 rounded-md hover:bg-white/10 uppercase tracking-[0.12em] text-xs text-white/80"
                      >
                        Industry
                        <ChevronDown className={`h-3 w-3 transition-transform ${mobileIndustriesOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {mobileIndustriesOpen && (
                        <div className="pl-3 flex flex-col">
                          {industries.map((industry) => (
                            <button
                              key={industry}
                              onClick={() => handleMobileIndustryClick(industry)}
                              className="text-left px-3 py-2 rounded-md hover:bg-white/10 text-[11px] uppercase tracking-[0.12em] text-white/70"
                            >
                              {getIndustryMenuLabel(industry)}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  <button onClick={() => goto('/expert-boards')} className="text-left px-3 py-3 rounded-md hover:bg-white/10 uppercase tracking-[0.12em] text-xs text-white/80">My Boards</button>
                  {user && !isAnonymous && <button onClick={() => goto('/my-conversations')} className="text-left px-3 py-3 rounded-md hover:bg-white/10 uppercase tracking-[0.12em] text-xs text-white/80">Conversations</button>}
                  <button onClick={() => goto('/templates')} className="text-left px-3 py-3 rounded-md hover:bg-white/10 uppercase tracking-[0.12em] text-xs text-white/80">All Templates</button>
                  <button onClick={() => goto('/applications')} className="text-left px-3 py-3 rounded-md hover:bg-white/10 uppercase tracking-[0.12em] text-xs text-white/80">My Apps</button>
                  <button onClick={() => goto(isAuthor ? '/for_expert' : '/become_expert')} className="text-left px-3 py-3 rounded-md hover:bg-white/10 uppercase tracking-[0.12em] text-xs text-white/80">For Experts</button>
                  {isAdmin && (
                    <button onClick={() => goto('/admin')} className="text-left px-3 py-3 rounded-md hover:bg-white/10 uppercase tracking-[0.12em] text-xs text-white/80">Admin</button>
                  )}
                </nav>
              )}

              <div className="border-t border-white/10 mt-2 p-3 space-y-2">
                {user && !isAnonymous ? (
                  !hideAuthenticatedActions && (
                    <>
                      <button
                        onClick={() => goto('/account')}
                        className="w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-2 text-xs font-medium text-white hover:bg-white/20"
                      >
                        <Zap className="w-3 h-3 text-yellow-300" />
                        {creditsLoading ? '…' : credits} credits
                      </button>
                      {user.email && (
                        <p className="text-[11px] text-white/60 text-center truncate">{user.email}</p>
                      )}
                      <button
                        onClick={() => { setMobileOpen(false); handleSignOut(); }}
                        className="w-full inline-flex items-center justify-center gap-1.5 text-xs uppercase tracking-[0.15em] text-white/80 hover:text-white py-2"
                      >
                        <LogOut className="w-3.5 h-3.5" />
                        Sign Out
                      </button>
                    </>
                  )
                ) : (
                  !hideUnauthenticatedActions && (
                    <>
                      <button
                        onClick={() => goto('/auth')}
                        className="w-full text-center text-xs uppercase tracking-[0.15em] text-white/80 hover:text-white py-2"
                      >
                        Sign In
                      </button>
                      <button
                        onClick={() => goto('/beta_access')}
                        className="w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] text-[#050d1f] hover:bg-white/90"
                      >
                        Apply for Beta Access
                      </button>
                    </>
                  )
                )}
                <button
                  onClick={() => goto(isAuthor ? '/for_expert' : '/become_expert')}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-white/80 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] text-white hover:bg-white hover:text-[#050d1f] transition-all"
                >
                  Become an Applyt Expert
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </SheetContent>
          </Sheet>

          <button
            type="button"
            onClick={() => navigate('/')}
            className="inline-flex flex-col items-center gap-1 focus:outline-none"
            aria-label="Applyt home"
          >
            <img src={logo} alt="Applyt" className="h-9 md:h-12 w-auto brightness-0 invert" />
            <div
              className="hidden sm:block text-[10px] md:text-xs uppercase tracking-[0.1em] text-white/70"
              style={{ transform: "translate(-5mm, -2mm)" }}
            >
              Expert AI Apps builder
            </div>
          </button>

          {/* Spacer to balance mobile layout */}
          <div className="w-10 md:hidden" aria-hidden="true" />

          <button
            onClick={() => navigate(isAuthor ? '/for_expert' : '/become_expert')}
            className="hidden md:inline-flex absolute right-4 sm:right-6 lg:right-8 items-center gap-2 rounded-full border border-white/80 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] text-white hover:bg-white hover:text-[#050d1f] transition-all"
          >
            Become an Applyt Expert
            <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Bottom tier: Navigation (desktop only) */}
      <div className="hidden md:block border-t border-b border-white/10 bg-gradient-to-r from-[#0a1836] via-[#08142b] to-[#0a1836]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-col gap-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            {showNavigation ? (
              <nav className="flex flex-wrap items-center gap-6 text-[12px] uppercase tracking-[0.15em] text-white/80">
                <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }} className="hover:text-white transition-colors">
                  Home
                </a>

                <DropdownMenu open={industriesOpen} onOpenChange={setIndustriesOpen}>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      disabled={industries.length === 0}
                      className="hover:text-white transition-colors flex items-center gap-1 text-[12px] uppercase tracking-[0.15em] text-white/80 disabled:cursor-not-allowed disabled:text-white/35"
                    >
                      Industry
                      <ChevronDown className={`h-3 w-3 transition-transform ${industriesOpen ? 'rotate-180' : ''}`} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className="max-h-80 w-60 overflow-y-auto border-white/15 bg-[#0a1836] p-2 text-white shadow-xl"
                  >
                    {industries.map((industry) => (
                      <DropdownMenuItem
                        key={industry}
                        onSelect={() => handleIndustryClick(industry)}
                        className="cursor-pointer rounded-md px-3 py-2 text-[11px] uppercase tracking-[0.12em] text-white/75 focus:bg-white/10 focus:text-white"
                      >
                        {getIndustryMenuLabel(industry)}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                <a href="/expert-boards" onClick={(e) => { e.preventDefault(); navigate('/expert-boards'); }} className="hover:text-white transition-colors">
                  My Boards
                </a>
                {user && !isAnonymous && (
                  <a href="/my-conversations" onClick={(e) => { e.preventDefault(); navigate('/my-conversations'); }} className="hover:text-white transition-colors">
                    Conversations
                  </a>
                )}

                <a href="/templates" onClick={(e) => { e.preventDefault(); navigate('/templates'); }} className="hover:text-white transition-colors">
                  Templates
                </a>
                <a href="/applications" onClick={(e) => { e.preventDefault(); navigate('/applications'); }} className="hover:text-white transition-colors">
                  My Apps
                </a>

                <a
                  href={isAuthor ? '/for_expert' : '/become_expert'}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(isAuthor ? '/for_expert' : '/become_expert');
                  }}
                  className="hover:text-white transition-colors"
                >
                  For Experts
                </a>
                {isAdmin && (
                  <a
                    href="/admin"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/admin');
                    }}
                    className="hover:text-white transition-colors"
                  >
                    Admin
                  </a>
                )}
              </nav>
            ) : (
              <div />
            )}

            <div className="flex items-center gap-3">
              {user && !isAnonymous ? (
                !hideAuthenticatedActions && (
                  <>
                    <button
                      onClick={() => navigate('/account')}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] font-medium text-white hover:bg-white/20 transition-colors"
                      title="View account & credits"
                    >
                      <Zap className="w-3 h-3 text-yellow-300" />
                      {creditsLoading ? '…' : credits} credits
                    </button>
                    <span className="text-[12px] text-white/60 hidden md:inline">
                      {user.email}
                    </span>
                    <button
                      onClick={handleSignOut}
                      className="text-[12px] uppercase tracking-[0.15em] text-white/80 hover:text-white transition-colors flex items-center gap-1"
                    >
                      <LogOut className="w-3.5 h-3.5" />
                      Sign Out
                    </button>
                  </>
                )
              ) : (
                !hideUnauthenticatedActions && (
                  <>
                    <button
                      onClick={handleSignIn}
                      className="text-[12px] uppercase tracking-[0.15em] text-white/80 hover:text-white transition-colors"
                    >
                      Sign In
                    </button>
                    <button
                      onClick={() => navigate('/beta_access')}
                      className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-[#050d1f] hover:bg-white/90 transition-colors"
                    >
                        Apply for Beta Access
                    </button>
                  </>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
